import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Save,
  Cancel,
  CheckCircle,
  Block,
  EmojiEvents,
  Settings,
  AssignmentInd
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';
import featuredService from '../../services/featuredService';
import providerService from '../../services/providerService';
import DialogHeader from '../Common/DialogHeader';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { formatHuf } from '../../utils/currency';
import AdminEarlyOfferPanel from './AdminEarlyOfferPanel';

const AdminFeaturedPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [tierLimits, setTierLimits] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Tier limit szerkesztés state-ek
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierFormData, setTierFormData] = useState({
    tier: 'TOP',
    maxSlots: 3,
    monthlyPrice: 15000,
    semiAnnualPrice: 80000,
    annualPrice: 150000,
    isActive: true
  });

  // Subscription szűrés state-ek
  const [subscriptionFilters, setSubscriptionFilters] = useState({
    status: '',
    tier: '',
    categoryId: ''
  });

  // Hozzárendelés dialog state-ek
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignFormData, setAssignFormData] = useState({
    tier: 'TOP',
    categoryId: '',
    endsAt: ''
  });
  const [providerSearch, setProviderSearch] = useState('');
  const [providerResults, setProviderResults] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);

  // Kategóriák betöltése
  useEffect(() => {
    loadCategories();
  }, []);

  // Tier limitek betöltése kategória változásakor
  useEffect(() => {
    if (selectedCategory) {
      loadTierLimits(selectedCategory);
    }
  }, [selectedCategory]);

  // Előfizetések betöltése szűrők változásakor
  useEffect(() => {
    if (tabValue === 1) {
      loadSubscriptions();
    }
  }, [tabValue, subscriptionFilters]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      if (response.success) {
        setCategories(response.data.categories);
        if (response.data.categories.length > 0 && !selectedCategory) {
          setSelectedCategory(response.data.categories[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Kategóriák betöltése sikertelen.');
    }
  };

  const loadTierLimits = async (categoryId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await featuredService.getCategoryTierLimits(categoryId);
      if (response.success) {
        setTierLimits(response.data);
      }
    } catch (err) {
      console.error('Error loading tier limits:', err);
      setError('Tier limitek betöltése sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await featuredService.getAllSubscriptions(subscriptionFilters);
      if (response.success) {
        setSubscriptions(response.data);
      }
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Előfizetések betöltése sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTierClick = (tier) => {
    if (tier) {
      // Meglévő tier szerkesztése
      setTierFormData({
        tier: tier.tier,
        maxSlots: tier.maxSlots,
        monthlyPrice: tier.monthlyPrice,
        semiAnnualPrice: tier.semiAnnualPrice || tier.monthlyPrice * 6,
        annualPrice: tier.annualPrice || tier.monthlyPrice * 12,
        isActive: tier.isActive
      });
      setEditingTier(tier);
    } else {
      // Új tier létrehozása
      setTierFormData({
        tier: 'TOP',
        maxSlots: 3,
        monthlyPrice: 15000,
        semiAnnualPrice: 80000,
        annualPrice: 150000,
        isActive: true
      });
      setEditingTier(null);
    }
    setEditDialogOpen(true);
  };

  const handleSaveTierLimit = async () => {
    if (!selectedCategory) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await featuredService.updateCategoryTierLimit(
        selectedCategory,
        tierFormData.tier,
        {
          maxSlots: parseInt(tierFormData.maxSlots),
          monthlyPrice: parseInt(tierFormData.monthlyPrice),
          semiAnnualPrice: parseInt(tierFormData.semiAnnualPrice),
          annualPrice: parseInt(tierFormData.annualPrice),
          isActive: tierFormData.isActive
        }
      );

      if (response.success) {
        setSuccess('Tier limit sikeresen mentve!');
        setEditDialogOpen(false);
        loadTierLimits(selectedCategory);
      } else {
        setError(response.message || 'Mentés sikertelen.');
      }
    } catch (err) {
      console.error('Error saving tier limit:', err);
      setError('Tier limit mentése sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateSubscription = async (subscriptionId) => {
    setLoading(true);
    try {
      const response = await featuredService.adminActivateSubscription(subscriptionId);
      if (response.success) {
        setSuccess('Előfizetés sikeresen aktiválva!');
        loadSubscriptions();
      } else {
        setError(response.message || 'Aktiválás sikertelen.');
      }
    } catch (err) {
      setError('Előfizetés aktiválása sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const handleExpireSubscription = async (subscriptionId) => {
    setLoading(true);
    try {
      const response = await featuredService.adminExpireSubscription(subscriptionId);
      if (response.success) {
        setSuccess('Előfizetés sikeresen lejáratva!');
        loadSubscriptions();
      } else {
        setError(response.message || 'Lejáratás sikertelen.');
      }
    } catch (err) {
      setError('Előfizetés lejáratása sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const searchProviders = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setProviderResults([]);
      return;
    }
    setProviderSearchLoading(true);
    try {
      const response = await providerService.getProviders({ search: term, limit: 10 });
      if (response.success) {
        setProviderResults(response.data.providers || []);
      }
    } catch (err) {
      console.error('Provider search error:', err);
    } finally {
      setProviderSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProviders(providerSearch), 300);
    return () => clearTimeout(timer);
  }, [providerSearch, searchProviders]);

  const handleOpenAssignDialog = () => {
    setAssignFormData({
      tier: 'TOP',
      categoryId: selectedCategory || (categories[0]?.id ?? ''),
      endsAt: ''
    });
    setProviderSearch('');
    setProviderResults([]);
    setSelectedProvider(null);
    setAssignDialogOpen(true);
  };

  const handleAssignTier = async () => {
    if (!selectedProvider || !assignFormData.categoryId || !assignFormData.tier || !assignFormData.endsAt) {
      setError('Minden mező kitöltése kötelező.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await featuredService.adminAssignTier(
        selectedProvider.id,
        assignFormData.categoryId,
        assignFormData.tier,
        assignFormData.endsAt
      );
      if (response.success) {
        const msg = response.slotsWarning
          ? 'Kiemelési szint hozzárendelve (slot limit túllépve).'
          : 'Kiemelési szint sikeresen hozzárendelve!';
        setSuccess(msg);
        setAssignDialogOpen(false);
        if (tabValue === 1) loadSubscriptions();
      } else {
        setError(response.message || 'Hozzárendelés sikertelen.');
      }
    } catch (err) {
      setError(err.message || 'Hozzárendelés sikertelen.');
    } finally {
      setLoading(false);
    }
  };

  const tierLabels = {
    // textColor: az arany háttéren a fehér szöveg olvashatatlan (~1.8:1), ott sötét szöveg kell
    TOP: { label: 'TOP', color: '#edbe5f', textColor: '#1b331f' },
    TIER2: { label: 'TIER 2', color: '#2a5d32', textColor: '#fff' },
    TIER3: { label: 'TIER 3', color: '#4f744d', textColor: '#fff' }
  };

  const statusLabels = {
    PENDING: { label: 'Függőben', color: 'warning' },
    ACTIVE: { label: 'Aktív', color: 'success' },
    SCHEDULED: { label: 'Megújítva (ütemezett)', color: 'info' },
    EXPIRED: { label: 'Lejárt', color: 'error' },
    CANCELLED: { label: 'Lemondva', color: 'default' },
    FAILED: { label: 'Sikertelen', color: 'error' }
  };

  return (
    <Box>
      {/* Állapot üzenetek */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabok */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Tier Limitek" icon={<Settings />} iconPosition="start" />
          <Tab label="Előfizetések" icon={<EmojiEvents />} iconPosition="start" />
          <Tab label="Korai ajánlat" icon={<AssignmentInd />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* TAB 0: Tier Limitek */}
      {tabValue === 2 && <AdminEarlyOfferPanel />}

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Kategória választó */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kategória kiválasztása
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Kategória</InputLabel>
                  <Select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Kategória"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Tier limitek táblázat */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Tier Limitek
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleEditTierClick(null)}
                    disabled={!selectedCategory}
                  >
                    Új Tier
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <ResponsiveTableContainer stickyLastColumn component="div">
                    <Table>
                      <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                        <TableRow>
                          <TableCell>Tier</TableCell>
                          <TableCell>Foglalt hely</TableCell>
                          <TableCell>Havi ár</TableCell>
                          <TableCell>Féléves ár</TableCell>
                          <TableCell>Éves ár</TableCell>
                          <TableCell>Aktív</TableCell>
                          <TableCell>Státusz</TableCell>
                          <TableCell>Műveletek</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tierLimits.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              Nincs beállított tier limit. Hozzon létre egyet!
                            </TableCell>
                          </TableRow>
                        ) : (
                          tierLimits.map((tier) => (
                            <TableRow key={tier.id}>
                              <TableCell>
                                <Chip
                                  label={tierLabels[tier.tier]?.label || tier.tier}
                                  sx={{
                                    backgroundColor: tierLabels[tier.tier]?.color,
                                    color: tierLabels[tier.tier]?.textColor || '#fff',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {tier.activeCount || 0} / {tier.maxSlots}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatHuf(tier.monthlyPrice)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatHuf(tier.semiAnnualPrice)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatHuf(tier.annualPrice)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={tier.activeCount || 0}
                                  color={tier.activeCount >= tier.maxSlots ? 'error' : 'success'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {tier.isActive ? (
                                  <Chip label="Aktív" color="success" size="small" />
                                ) : (
                                  <Chip label="Inaktív" color="default" size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditTierClick(tier)}
                                >
                                  <Edit />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ResponsiveTableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 1: Előfizetések */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Szűrők */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Szűrők
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Státusz</InputLabel>
                      <Select
                        value={subscriptionFilters.status}
                        onChange={(e) => setSubscriptionFilters({ ...subscriptionFilters, status: e.target.value })}
                        label="Státusz"
                      >
                        <MenuItem value="">Összes</MenuItem>
                        <MenuItem value="PENDING">Függőben</MenuItem>
                        <MenuItem value="ACTIVE">Aktív</MenuItem>
                        <MenuItem value="SCHEDULED">Megújítva (ütemezett)</MenuItem>
                        <MenuItem value="EXPIRED">Lejárt</MenuItem>
                        <MenuItem value="CANCELLED">Lemondva</MenuItem>
                        <MenuItem value="FAILED">Sikertelen</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Tier</InputLabel>
                      <Select
                        value={subscriptionFilters.tier}
                        onChange={(e) => setSubscriptionFilters({ ...subscriptionFilters, tier: e.target.value })}
                        label="Tier"
                      >
                        <MenuItem value="">Összes</MenuItem>
                        <MenuItem value="TOP">TOP</MenuItem>
                        <MenuItem value="TIER2">TIER 2</MenuItem>
                        <MenuItem value="TIER3">TIER 3</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Kategória</InputLabel>
                      <Select
                        value={subscriptionFilters.categoryId}
                        onChange={(e) => setSubscriptionFilters({ ...subscriptionFilters, categoryId: e.target.value })}
                        label="Kategória"
                      >
                        <MenuItem value="">Összes</MenuItem>
                        {categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Előfizetések táblázat */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Előfizetések
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AssignmentInd />}
                    onClick={handleOpenAssignDialog}
                  >
                    Kiemelés hozzárendelése
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <ResponsiveTableContainer stickyLastColumn component="div">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Szolgáltató</TableCell>
                          <TableCell>Kategória</TableCell>
                          <TableCell>Tier</TableCell>
                          <TableCell>Időszak</TableCell>
                          <TableCell>Státusz</TableCell>
                          <TableCell>Kezdés</TableCell>
                          <TableCell>Lejárat</TableCell>
                          <TableCell>Ár</TableCell>
                          <TableCell>Műveletek</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subscriptions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              Nincs megjeleníthető előfizetés.
                            </TableCell>
                          </TableRow>
                        ) : (
                          subscriptions.map((sub) => (
                            <TableRow key={sub.id}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {sub.provider?.businessName || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {sub.provider?.user?.email}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{sub.category?.name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={tierLabels[sub.tier]?.label || sub.tier}
                                  sx={{
                                    backgroundColor: tierLabels[sub.tier]?.color,
                                    color: tierLabels[sub.tier]?.textColor || '#fff',
                                    fontWeight: 'bold'
                                  }}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {sub.billingPeriod === 'MONTHLY' && 'Havi'}
                                {sub.billingPeriod === 'SEMI_ANNUAL' && 'Féléves'}
                                {sub.billingPeriod === 'ANNUAL' && 'Éves'}
                                {sub.billingPeriod === 'MANUAL' && (
                                  <Chip label="Manuális" size="small" color="info" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={statusLabels[sub.status]?.label || sub.status}
                                  color={statusLabels[sub.status]?.color || 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {sub.startsAt ? new Date(sub.startsAt).toLocaleDateString('hu-HU') : '-'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {sub.endsAt ? new Date(sub.endsAt).toLocaleDateString('hu-HU') : '-'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatHuf(sub.paidAmount, { fallback: '0 Ft' })}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {sub.status === 'PENDING' && (
                                    <Tooltip title="Aktiválás">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleActivateSubscription(sub.id)}
                                      >
                                        <CheckCircle />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {sub.status === 'ACTIVE' && (
                                    <Tooltip title="Lejáratás">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleExpireSubscription(sub.id)}
                                      >
                                        <Block />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ResponsiveTableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tier Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<Settings />}
            title={editingTier ? 'Tier Limit Szerkesztése' : 'Új Tier Limit Létrehozása'}
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Tier Szint</InputLabel>
                <Select
                  value={tierFormData.tier}
                  onChange={(e) => setTierFormData({ ...tierFormData, tier: e.target.value })}
                  label="Tier Szint"
                  disabled={!!editingTier} // Meglévő tier esetén nem változtatható
                >
                  <MenuItem value="TOP">TOP</MenuItem>
                  <MenuItem value="TIER2">TIER 2</MenuItem>
                  <MenuItem value="TIER3">TIER 3</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Maximális Slot-ok Száma"
                type="number"
                value={tierFormData.maxSlots}
                onChange={(e) => setTierFormData({ ...tierFormData, maxSlots: e.target.value })}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Havi Ár (HUF)"
                type="number"
                value={tierFormData.monthlyPrice}
                onChange={(e) => setTierFormData({ ...tierFormData, monthlyPrice: e.target.value })}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Féléves Ár (HUF)"
                type="number"
                value={tierFormData.semiAnnualPrice}
                onChange={(e) => setTierFormData({ ...tierFormData, semiAnnualPrice: e.target.value })}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Éves Ár (HUF)"
                type="number"
                value={tierFormData.annualPrice}
                onChange={(e) => setTierFormData({ ...tierFormData, annualPrice: e.target.value })}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Státusz</InputLabel>
                <Select
                  value={String(tierFormData.isActive)}
                  onChange={(e) => setTierFormData({ ...tierFormData, isActive: e.target.value === 'true' })}
                  label="Státusz"
                >
                  <MenuItem value="true">Aktív</MenuItem>
                  <MenuItem value="false">Inaktív</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSaveTierLimit}
            disabled={loading}
          >
            Mentés
          </Button>
        </DialogActions>
      </Dialog>
      {/* Kiemelés hozzárendelése Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<AssignmentInd />}
            title="Kiemelési szint hozzárendelése"
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Szolgáltató keresés */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Szolgáltató keresése (névvel)"
                value={providerSearch}
                onChange={(e) => {
                  setProviderSearch(e.target.value);
                  setSelectedProvider(null);
                }}
                InputProps={{
                  endAdornment: providerSearchLoading && <CircularProgress size={18} />
                }}
              />
              {!selectedProvider && providerResults.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {providerResults.map((p) => (
                      <ListItem key={p.id} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            setSelectedProvider(p);
                            setProviderSearch(p.businessName);
                            setProviderResults([]);
                          }}
                        >
                          <ListItemText
                            primary={p.businessName}
                            secondary={p.user?.email}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
              {selectedProvider && (
                <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                  Kiválasztva: <strong>{selectedProvider.businessName}</strong> ({selectedProvider.user?.email})
                </Alert>
              )}
            </Grid>

            {/* Kategória */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Kategória</InputLabel>
                <Select
                  value={assignFormData.categoryId}
                  onChange={(e) => setAssignFormData({ ...assignFormData, categoryId: e.target.value })}
                  label="Kategória"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tier */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Kiemelési szint</InputLabel>
                <Select
                  value={assignFormData.tier}
                  onChange={(e) => setAssignFormData({ ...assignFormData, tier: e.target.value })}
                  label="Kiemelési szint"
                >
                  <MenuItem value="TOP">
                    <Chip label="TOP" size="small" sx={{ backgroundColor: tierLabels.TOP.color, color: tierLabels.TOP.textColor, mr: 1 }} />
                    TOP
                  </MenuItem>
                  <MenuItem value="TIER2">
                    <Chip label="TIER 2" size="small" sx={{ backgroundColor: tierLabels.TIER2.color, color: tierLabels.TIER2.textColor, mr: 1 }} />
                    TIER 2
                  </MenuItem>
                  <MenuItem value="TIER3">
                    <Chip label="TIER 3" size="small" sx={{ backgroundColor: tierLabels.TIER3.color, color: tierLabels.TIER3.textColor, mr: 1 }} />
                    TIER 3
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Lejárat dátuma */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Kiemelés vége"
                type="date"
                value={assignFormData.endsAt}
                onChange={(e) => setAssignFormData({ ...assignFormData, endsAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" icon={false}>
                A hozzárendelés fizetés nélkül, azonnal AKTÍV státusszal jön létre. Ha a szolgáltatónak már van aktív kiemelése ebben a kategóriában, az lejár.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={loading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <AssignmentInd />}
            onClick={handleAssignTier}
            disabled={loading || !selectedProvider}
          >
            Hozzárendelés
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFeaturedPage;